/**
 * 연결(DB 접속정보) 관리 — 무설치판 전용.
 *
 * 왜 여기에 있나: Airflow 를 쓰는 배포에서는 연결이 Airflow 에 있고 거기서 관리한다. 두 곳에서 고칠 수
 * 있게 하면 어느 쪽이 진짜인지 알 수 없게 되므로, 그 배포에서는 서버가 이 화면을 막고 그 사실을 말해 준다.
 *
 * 비밀번호는 **서버가 내보내지 않는다**(들어 있는지만 알려 준다). 그래서 빈 칸으로 저장하면 기존 값을
 * 그대로 둔다 — 이름만 고치려던 사람이 접속을 잃지 않도록. 지우려면 '비밀번호 지우기' 를 누른다.
 *
 * 웹 버전(2026-10-07): 연결마다 **저장 위치**를 고른다.
 *   · 서버     Airflow 연결(암호화). 함께 쓰고 예약 실행이 된다 — 관리자만 만든다
 *   · 내 PC    접속 정보는 이 브라우저에만 암호화해 둔다(services/localVault). 서버는 이름·종류만 안다.
 *              고객정보처럼 기밀 DB 용. 「실행」 할 때만 값을 보내고, 예약 실행에는 쓸 수 없다(서버 services/local_conns)
 */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store';
import { fetchConnections } from '../../store/connectionsSlice';
import { be1Fetch } from '../../config/api';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { showAlert, showConfirm } from '../common/AppDialog';
import { logUsage } from '../../services/eventLog';
import DriverSettingsPanel from './DriverSettingsPanel';
import { useTranslation } from 'react-i18next';
import { errorText, errorFromResponse } from '../../services/errorText';
import { loadCapabilities, useCapabilities } from '../../services/capabilities';
import { deletePcConn, listPcConns, readPcConn, savePcConn, testPcConn } from '../../services/pcConnections';

interface ConnRow {
  connection_id: string;
  conn_type: string;
  host: string;
  port: number | null;
  login: string;
  schema: string;
  extra?: Record<string, any>;
  hasPassword: boolean;
  /** 웹 — 저장 위치 */
  storage?: 'server' | 'local';
  /** 내 PC 연결 — 이 브라우저에 값이 있는가 */
  here?: boolean;
  /** 무설치판 — 다른 PC·다른 키로 봉인되어 풀 수 없다 */
  unreadable?: boolean;
}

interface Draft {
  id: string;
  isNew: boolean;
  conn_type: string;
  host: string;
  port: string;
  login: string;
  schema: string;
  password: string;
  clearPassword: boolean;
  /** 오라클: 입력값을 서비스명으로 보낼지 SID 로 보낼지. 예전에는 칸 이름만 '서비스명 / SID' 이고 늘 SID 로
   *  보내서, 서비스명을 적은 현장이 ORA-12505 로 붙지 못했다(2026-09-17). 보통은 서비스명이 맞다. */
  oracleTarget: 'service' | 'sid';
  /** 서버에 저장돼 있던 extra — 화면에 없는 키(connect_args 등)를 저장하면서 지우지 않으려고 들고 있다 */
  extra: Record<string, any>;
  /** 웹 — 저장 위치 */
  storage: 'server' | 'local';
}

const EMPTY: Draft = {
  id: '', isNew: true, conn_type: 'postgres', host: '', port: '', login: '', schema: '',
  password: '', clearPassword: false, oracleTarget: 'service', extra: {}, storage: 'server',
};

/** 내 PC 연결로 고를 수 있는 종류 — SQLite 는 서버의 파일이라 뜻이 없다 */
const WEB_TYPES = ['postgres', 'mysql', 'oracle', 'tibero'];

const parseExtra = (x: any): Record<string, any> => {
  if (x && typeof x === 'object') return x;
  try { const v = JSON.parse(String(x || '{}')); return v && typeof v === 'object' ? v : {}; } catch { return {}; }
};

const DEFAULT_PORT: Record<string, string> = { postgres: '5432', mysql: '3306', oracle: '1521', tibero: '8629' };

interface Props {
  onClose: () => void;
  /** 열자마자 이 이름·종류로 새 연결을 쓰기 시작한다 — 테라스트림 변환 창의 「연결 만들기」 */
  create?: { id: string; conn_type: string };
}

const ConnectionsDialog: React.FC<Props> = ({ onClose, create }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const [rows, setRows] = useState<ConnRow[]>([]);
  const [types, setTypes] = useState<string[]>(['postgres', 'mysql', 'oracle', 'tibero', 'sqlite']);
  /** 종류별로 이 서버에 드라이버가 있는지 — 없는 것은 고르지 못하게 하되 **이유를 보여 준다**.
   *  목록에서 지워 버리면 사용자가 아는 것은 '없다' 뿐이라, 무엇을 하면 생기는지 알 길이 없다. */
  const [usable, setUsable] = useState<Record<string, boolean>>({});
  /** '연결' 과 '드라이버' 탭 — 드라이버 위치는 연결과 같은 자리에서 고친다(LiveSQL 과 같은 배치) */
  const [tab, setTab] = useState<'connections' | 'drivers'>('connections');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  /** 노드 속성 탭의 연결 드롭다운이 보는 목록(redux)을 새로 받는다.
   *  그 목록은 앱이 뜰 때 한 번만 받아서, 여기서 추가·삭제해도 속성 탭에는 새 연결이 보이지 않았다
   *  (무설치판 현장 보고 2026-09-17). 이 창에서 목록을 바꾼 뒤에는 반드시 부른다. */
  const refreshSharedList = () => { dispatch(fetchConnections()); };
  /** 서버가 '여기서는 못 고친다' 고 한 경우 — 화면을 막고 이유를 보인다 */
  const [blocked, setBlocked] = useState('');
  const [error, setError] = useState('');
  /** 웹 버전 — 서버 연결은 Airflow, 내 PC 연결은 이 브라우저. 저장 위치를 고른다 */
  const [web, setWeb] = useState(false);
  const caps = useCapabilities();
  const isAdmin = !!caps?.admin;

  /** 웹 — 서버 연결(Airflow)과 내 PC 연결을 한 표로 */
  const loadWeb = async () => {
    const res = await be1Fetch('/api/connections/', { credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    const list: any[] = await res.json();
    let here = new Set<string>();
    try { here = new Set((await listPcConns()).here); } catch { /* 로그인 전 등 — 금고만 못 본다 */ }
    // 내 PC 연결의 주소·계정은 서버에 없다 — 이 PC 금고에서 읽어 보여 준다(이 사람의 PC 이므로)
    const mine: Record<string, any> = {};
    for (const c of list) {
      if (c?.storage === 'local' && here.has(c.connection_id)) {
        const v = await readPcConn(c.connection_id);
        if (v) mine[c.connection_id] = { host: v.host, port: v.port, login: v.login, schema: v.schema, extra: v.extra };
      }
    }
    setRows(list.filter((c) => c && c.connection_id && c.connection_id !== 'test_conn').map((c) => ({
      connection_id: c.connection_id, conn_type: c.conn_type || '',
      host: mine[c.connection_id]?.host ?? (c.host || ''), port: mine[c.connection_id]?.port ?? (c.port || null),
      login: mine[c.connection_id]?.login ?? (c.login || ''), schema: mine[c.connection_id]?.schema ?? (c.schema || ''),
      extra: mine[c.connection_id]?.extra ?? parseExtra(c.extra),
      hasPassword: c.storage === 'local' ? here.has(c.connection_id) : true,
      storage: c.storage === 'local' ? 'local' : 'server', here: here.has(c.connection_id),
    })));
    setTypes(WEB_TYPES);
    setWeb(true);
    setBlocked('');
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      // 웹 버전이면 무설치판 편집 목록(/manage)을 묻지 않는다 — 늘 400 이라 실패 기록만 쌓인다
      const caps0 = await loadCapabilities();
      if (caps0 && caps0.localConnections === false) {
        await loadWeb();
        return;
      }
      const res = await be1Fetch('/api/connections/manage', { credentials: 'include' });
      const body = await res.json();
      if (res.status === 400) {
        // 웹 버전 — 이 창에서 서버(Airflow) 연결과 내 PC 연결을 다룬다
        try { await loadWeb(); } catch (e: any) { setBlocked(errorText(body, t('connection:dlg.blocked')) + (e?.message ? ` (${e.message})` : '')); }
        return;
      }
      if (!res.ok) throw new Error(errorText(body, res.statusText));
      setRows(body.items || []);
      if (body.connTypes?.length) setTypes(body.connTypes);
      if (body.usableTypes) setUsable(body.usableTypes);
      setBlocked('');
    } catch (e: any) {
      setError(e?.message || t('connection:dlg.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (create) {
      const type = create.conn_type || 'postgres';
      setDraft({ ...EMPTY, id: create.id, conn_type: type, port: DEFAULT_PORT[type] || '' });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const edit = async (r: ConnRow) => {
    let extra = r.extra || {};
    if (web && r.storage === 'local') {
      // 내 PC 연결 — 값은 이 브라우저 금고에서
      const v = await readPcConn(r.connection_id);
      if (v) r = { ...r, conn_type: v.conn_type || r.conn_type, host: v.host, port: v.port, login: v.login, schema: v.schema, extra: v.extra || {} };
      extra = r.extra || {};
    } else if (web) {
      try {
        const res = await be1Fetch(`/api/connections/server/${encodeURIComponent(r.connection_id)}`, { credentials: 'include' });
        if (res.ok) { const v = await res.json(); r = { ...r, ...v, extra: parseExtra(v.extra) }; extra = r.extra || {}; }
      } catch { /* 목록의 값으로 */ }
    }
    // 오라클은 extra.service_name 이 있으면 서비스명, 없고 schema 가 있으면 SID 로 저장된 것이다
    const service = typeof extra.service_name === 'string' ? extra.service_name : '';
    const target: Draft['oracleTarget'] = service || !r.schema ? 'service' : 'sid';
    setDraft({
      id: r.connection_id, isNew: false, conn_type: r.conn_type, host: r.host,
      port: r.port ? String(r.port) : '', login: r.login,
      schema: r.conn_type === 'oracle' && target === 'service' ? service : r.schema,
      password: '', clearPassword: false, oracleTarget: target, extra, storage: r.storage || 'server',
    });
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        conn_type: draft.conn_type, host: draft.host, login: draft.login, schema: draft.schema,
        port: draft.port ? Number(draft.port) : null,
      };
      // 오라클: 서비스명은 extra.service_name 으로, SID 는 schema 로 — 둘을 함께 보내면 드라이버가 거절한다.
      // extra 의 다른 키는 그대로 둔다. 오라클이 아니면 남아 있던 service_name 만 걷어 낸다
      // (종류를 바꿨는데 남아 있으면 다른 DB 의 접속 주소에 ?service_name= 이 붙는다).
      const { service_name: _dropped, ...otherExtra } = draft.extra || {};
      if (draft.conn_type === 'oracle') {
        if (draft.oracleTarget === 'service') {
          body.extra = { ...otherExtra, service_name: draft.schema.trim() };
          body.schema = '';
        } else {
          body.extra = otherExtra;
        }
      } else if (_dropped !== undefined) {
        body.extra = otherExtra;
      }
      // 비밀번호는 **준 적이 있을 때만** 보낸다. 빈 칸을 그대로 보내면 서버가 지워 버린다.
      if (draft.clearPassword) body.password = '';
      else if (draft.password) body.password = draft.password;

      if (web) {
        const id = draft.id.trim();
        if (draft.storage === 'local') {
          // 내 PC — 이 브라우저 금고에. 비밀번호 칸이 비었으면 이 PC 에 있던 값을 그대로
          const prev = draft.isNew ? null : await readPcConn(id);
          await savePcConn(id, {
            conn_type: draft.conn_type, host: draft.host.trim(), port: draft.port ? Number(draft.port) : null,
            login: draft.login.trim(), schema: String(body.schema ?? '').trim(),
            password: draft.clearPassword ? '' : (draft.password || prev?.password || ''),
            extra: (body.extra as Record<string, any> | undefined) ?? (draft.extra || {}),
          });
        } else {
          const res = await be1Fetch(`/api/connections/server/${encodeURIComponent(id)}`, {
            method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...body, extra: body.extra ?? draft.extra ?? {} }),
          });
          if (!res.ok) throw await errorFromResponse(res);
        }
        setDraft(null);
        await load();
        refreshSharedList();
        return;
      }

      const res = await be1Fetch(`/api/connections/manage/${encodeURIComponent(draft.id.trim())}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const out = await res.json();
      if (!res.ok) throw new Error(errorText(out, res.statusText));
      setDraft(null);
      await load();
      refreshSharedList();
    } catch (e: any) {
      showAlert(t('connection:dlg.saveFailed', { message: e?.message || '' }), { title: t('connection:dlg.connection'), tone: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  const test = async (id: string) => {
    setBusy(true);
    try {
      const row = rows.find((r) => r.connection_id === id);
      if (web && row?.storage === 'local') {
        try {
          await testPcConn(id);
        } catch (e: any) {
          throw new Error(e?.message === 'missing' ? t('connection:dlg.pcMissing') : e?.message);
        }
        logUsage('connection.test', { target: id, detail: { storage: 'local' } });
        showAlert(t('connection:dlg.testOk', { id }), { title: t('connection:dlg.testTitle') });
        return;
      }
      const res = await be1Fetch(web ? `/api/connections/server/${encodeURIComponent(id)}/test` : `/api/connections/manage/${encodeURIComponent(id)}/test`, {
        method: 'POST', credentials: 'include',
      });
      const out = await res.json();
      logUsage('connection.test', { target: id });
      if (!res.ok) throw new Error(errorText(out, res.statusText));
      showAlert(t('connection:dlg.testOk', { id }), { title: t('connection:dlg.testTitle') });
    } catch (e: any) {
      showAlert(t('connection:dlg.testFailed', { message: e?.message || '' }), { title: t('connection:dlg.testTitle'), tone: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!(await showConfirm(t('connection:dlg.deleteConfirm', { id }),
      { title: t('connection:dlg.deleteTitle'), tone: 'danger' }))) return;
    setBusy(true);
    try {
      const row = rows.find((r) => r.connection_id === id);
      if (web && row?.storage === 'local') {
        await deletePcConn(id);
      } else {
        const res = await be1Fetch(web ? `/api/connections/server/${encodeURIComponent(id)}` : `/api/connections/manage/${encodeURIComponent(id)}`, {
          method: 'DELETE', credentials: 'include',
        });
        if (!res.ok) throw await errorFromResponse(res);
      }
      await load();
      refreshSharedList();
    } catch (e: any) {
      showAlert(t('connection:dlg.deleteFailed', { message: e?.message || '' }), { title: t('connection:dlg.connection'), tone: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  const field = (label: string, node: React.ReactNode, hint?: string) => (
    <label className="block">
      <span className="text-xs text-gray-600">{label}</span>
      {node}
      {hint && <span className="block text-[0.6875rem] text-gray-400 mt-0.5">{hint}</span>}
    </label>
  );

  const inputCls = 'w-full rounded border border-gray-300 px-2 py-1 text-sm mt-0.5';

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-50 whitespace-normal text-left"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-[820px] max-w-[95vw] max-h-[88vh] bg-white rounded-lg shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50 shrink-0">
          <div className="flex items-center gap-4">
            <div className="font-semibold text-gray-900">{t('connection:dlg.title')}</div>
            <div className="flex gap-1">
              {([['connections', 'connection:dlg.tabConnections'], ['drivers', 'connection:dlg.tabDrivers']] as const).map(([key, labelKey]) => (
                <button key={key} type="button" onClick={() => setTab(key)}
                  className={'text-xs px-2.5 py-1 rounded ' + (tab === key
                    ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-600 hover:bg-gray-100')}>
                  {t(labelKey)}
                </button>
              ))}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label={t('connection:dlg.close')}>✕</button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          {tab === 'drivers' && <DriverSettingsPanel />}
          {tab === 'connections' && loading && <div className="text-sm text-gray-500">{t('connection:dlg.reading')}</div>}
          {tab === 'connections' && error && <div className="text-sm text-red-600">{error}</div>}

          {tab === 'connections' && blocked && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {blocked}
            </div>
          )}

          {tab === 'connections' && !loading && !error && !blocked && (
            <>
              {web && <div className="mb-2 text-xs text-gray-600">{t('connection:dlg.webIntro')}</div>}
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="py-1.5">{t('connection:dlg.colName')}</th>
                    <th className="py-1.5 w-24">{t('connection:dlg.colType')}</th>
                    <th className="py-1.5">{t('connection:dlg.colAddress')}</th>
                    <th className="py-1.5 w-28">{t('connection:dlg.colUser')}</th>
                    {web && <th className="py-1.5 w-28">{t('connection:dlg.colStorage')}</th>}
                    <th className="py-1.5 w-20">{t('connection:dlg.colPassword')}</th>
                    <th className="py-1.5 w-40" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.connection_id} className="border-b border-gray-100">
                      <td className="py-1.5 font-medium text-gray-800">{r.connection_id}</td>
                      <td className="py-1.5 text-gray-600">{r.conn_type}</td>
                      <td className="py-1.5 text-gray-600">
                        {r.host}{r.port ? `:${r.port}` : ''}
                        {r.extra?.service_name
                          ? ` / ${r.extra.service_name} (${t('connection:dlg.serviceName')})`
                          : (r.schema ? ` / ${r.schema}${r.conn_type === 'oracle' ? ' (SID)' : ''}` : '')}
                      </td>
                      <td className="py-1.5 text-gray-600">{r.login}</td>
                      {web && (
                        <td className="py-1.5 text-xs">
                          {r.storage === 'local'
                            ? <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700" title={t('connection:dlg.storagePcHint')}>🔒 {t('connection:dlg.storagePc')}</span>
                            : <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600" title={t('connection:dlg.storageServerHint')}>{t('connection:dlg.storageServer')}</span>}
                        </td>
                      )}
                      <td className="py-1.5 text-xs text-gray-500">
                        {r.unreadable ? <span className="text-amber-700">{t('connection:dlg.unreadable')}</span>
                          : web && r.storage === 'local' ? (r.here ? t('connection:dlg.pcHere') : <span className="text-amber-700">{t('connection:dlg.pcNotHere')}</span>)
                            : web ? '—' : (r.hasPassword ? t('connection:dlg.hasPassword') : t('connection:dlg.noPassword'))}
                      </td>
                      <td className="py-1.5 text-right space-x-1">
                        <button type="button" disabled={busy} onClick={() => test(r.connection_id)}
                          className="text-xs px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50">{t('connection:dlg.test')}</button>
                        {(!web || r.storage === 'local' || isAdmin) && (
                          <>
                            <button type="button" disabled={busy} onClick={() => edit(r)}
                              className="text-xs px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50">{t('connection:dlg.edit')}</button>
                            <button type="button" disabled={busy} onClick={() => remove(r.connection_id)}
                              className="text-xs px-2 py-0.5 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50">{t('connection:dlg.delete')}</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={web ? 7 : 6} className="py-6 text-center text-gray-500">
                      {t('connection:dlg.empty')}
                    </td></tr>
                  )}
                </tbody>
              </table>

              {!draft && (
                <button type="button" onClick={() => setDraft({ ...EMPTY, port: DEFAULT_PORT.postgres, storage: web && !isAdmin ? 'local' : 'server' })}
                  className="mt-3 text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">
                  {t('connection:dlg.add')}
                </button>
              )}

              {draft && (
                <div className="mt-3 rounded border border-blue-200 bg-blue-50/40 p-3">
                  <div className="text-sm font-medium text-gray-800 mb-2">
                    {draft.isNew ? t('connection:dlg.newTitle') : t('connection:dlg.editTitle', { id: draft.id })}
                  </div>
                  {web && (
                    <div className="mb-3">
                      <div className="text-xs text-gray-600 mb-1">{t('connection:dlg.storage')}</div>
                      <div className="grid grid-cols-2 gap-2">
                        {(['server', 'local'] as const).map((st) => {
                          const disabled = !draft.isNew || (st === 'server' && !isAdmin);
                          const on = draft.storage === st;
                          return (
                            <label key={st} className={'flex items-start gap-2 rounded border px-2.5 py-2 text-xs ' + (on ? 'border-blue-500 bg-white' : 'border-gray-200 bg-white/60')
                              + (disabled && !on ? ' opacity-50' : '')}>
                              <input type="radio" name="conn-storage" className="mt-0.5" checked={on} disabled={disabled}
                                onChange={() => setDraft({ ...draft, storage: st })} />
                              <span>
                                <span className="font-semibold text-gray-800">{st === 'local' ? `🔒 ${t('connection:dlg.storagePc')}` : t('connection:dlg.storageServer')}</span>
                                <span className="block text-gray-500 mt-0.5">{st === 'local' ? t('connection:dlg.storagePcHint') : t('connection:dlg.storageServerHint')}</span>
                                {st === 'server' && !isAdmin && <span className="block text-amber-700 mt-0.5">{t('connection:dlg.storageServerAdminOnly')}</span>}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {!draft.isNew && <div className="text-[0.6875rem] text-gray-400 mt-1">{t('connection:dlg.storageFixed')}</div>}
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3">
                    {field(t('connection:dlg.name'), (
                      <input value={draft.id} disabled={!draft.isNew}
                        onChange={(e) => setDraft({ ...draft, id: e.target.value })}
                        className={inputCls + (draft.isNew ? '' : ' bg-gray-100 text-gray-500')} />
                    ), draft.isNew ? t('connection:dlg.nameHintNew') : t('connection:dlg.nameHintFixed'))}

                    {field(t('connection:dlg.type'), (
                      <select value={draft.conn_type} className={inputCls}
                        onChange={(e) => setDraft({
                          ...draft, conn_type: e.target.value,
                          port: DEFAULT_PORT[e.target.value] || draft.port,
                        })}>
                        {types.map((ct) => (
                          <option key={ct} value={ct} disabled={usable[ct] === false}>
                            {ct}{usable[ct] === false ? t('connection:dlg.noDriver') : ''}
                          </option>
                        ))}
                      </select>
                    ), usable[draft.conn_type] === false
                      ? (draft.conn_type === 'tibero'
                        ? t('connection:dlg.tiberoNeedsDriver')
                        : t('connection:dlg.driverMissing'))
                      : undefined)}

                    {field(t('connection:dlg.port'), (
                      <input value={draft.port} inputMode="numeric"
                        onChange={(e) => setDraft({ ...draft, port: e.target.value })} className={inputCls} />
                    ))}

                    {/* SQLite 는 서버가 아니라 **파일 하나**다 — 같은 칸에 경로를 적는다(포트·계정은 쓰지 않는다) */}
                    {field(draft.conn_type === 'sqlite' ? t('connection:dlg.dbFile') : t('connection:dlg.host'), (
                      <input value={draft.host}
                        placeholder={draft.conn_type === 'sqlite' ? t('connection:dlg.dbFileHint') : '192.168.0.10'}
                        onChange={(e) => setDraft({ ...draft, host: e.target.value })} className={inputCls} />
                    ))}

                    {field(t('connection:dlg.user'), (
                      <input value={draft.login}
                        onChange={(e) => setDraft({ ...draft, login: e.target.value })} className={inputCls} />
                    ))}

                    {draft.conn_type === 'oracle'
                      ? field(draft.oracleTarget === 'service' ? t('connection:dlg.serviceNameLabel') : t('connection:dlg.sid'), (
                        <div>
                          <div className="flex gap-1 mt-0.5 mb-1">
                            {([['service', 'connection:dlg.serviceNameLabel'], ['sid', 'connection:dlg.sid']] as const).map(([key, labelKey]) => (
                              <button key={key} type="button" onClick={() => setDraft({ ...draft, oracleTarget: key })}
                                className={'text-xs px-2 py-0.5 rounded border ' + (draft.oracleTarget === key
                                  ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                                  : 'border-gray-300 text-gray-600 hover:bg-gray-100')}>
                                {t(labelKey)}
                              </button>
                            ))}
                          </div>
                          <input value={draft.schema}
                            onChange={(e) => setDraft({ ...draft, schema: e.target.value })} className={inputCls} />
                        </div>
                      ), t('connection:dlg.oracleHint'))
                      : field(draft.conn_type === 'tibero' ? t('connection:dlg.tiberoDb') : t('connection:dlg.database'), (
                        <input value={draft.schema}
                          onChange={(e) => setDraft({ ...draft, schema: e.target.value })} className={inputCls} />
                      ))}

                    <div className="col-span-2">
                      {field(t('connection:dlg.password'), (
                        <input type="password" value={draft.password} disabled={draft.clearPassword}
                          placeholder={draft.isNew ? '' : t('connection:dlg.passwordKeep')}
                          onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                          className={inputCls + (draft.clearPassword ? ' bg-gray-100' : '')} />
                      ), draft.isNew ? undefined : t('connection:dlg.passwordHidden'))}
                    </div>
                    {!draft.isNew && (
                      <label className="flex items-end gap-1.5 pb-1 text-xs text-gray-600">
                        <input type="checkbox" checked={draft.clearPassword}
                          onChange={(e) => setDraft({ ...draft, clearPassword: e.target.checked, password: '' })} />
                        {t('connection:dlg.clearPassword')}
                      </label>
                    )}
                  </div>

                  <div className="mt-3 flex justify-end gap-2">
                    <button type="button" onClick={() => setDraft(null)} disabled={busy}
                      className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">{t('connection:dlg.cancel')}</button>
                    <button type="button" onClick={save} disabled={busy || !draft.id.trim()}
                      className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300">
                      {busy ? t('connection:dlg.saving') : t('connection:dlg.save')}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-between items-center px-4 py-2.5 border-t border-gray-200 bg-gray-50 shrink-0">
          <span className="text-[0.6875rem] text-gray-500">
            {t('connection:dlg.oracleFooter')}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={load} disabled={loading || busy}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50">{t('connection:dlg.refresh')}</button>
            <button type="button" onClick={onClose}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">{t('connection:dlg.close')}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionsDialog;
